import { ChangeEvent, FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { TextField, Autocomplete } from "@mui/material";

import { faChevronDown, faExternalLink } from "@fortawesome/pro-regular-svg-icons";

import { Link } from "react-router-dom";

import { ERROR_TYPE, getAllErrors, renderErrorMessages, useSearch } from "@/utils";

import { Button } from "@/components/Button/Button";

import { InputField } from "@/components/InputField/InputField";

import { ButtonColor } from "@/components/Button/types";

import { EmploymentType, ScrapeLevel } from "@/enum/job.enum";
import {
	useCreateJobMutation,
	usePreviewJobMutation,
	useUpdateJobMutation,
} from "@/pages/Private/redux/job/job.api";
import {
	AutoComplete,
	AutoCompleteItem as AutoCompleteItemType,
} from "@/components/AutoComplete/AutoComplete";

import { useLazyGetFilterByPropertyQuery } from "@/pages/Private/redux/list/list.api";

import { Icon, Modal } from "@/components";

import { Toggle } from "@/components/Toggle/Toggle";

import { Information } from "@/components/Information/Information";

import { Table, TableColumn } from "@/components/Table/Table";

import { LinkedinIcon } from "@/components/LinkedinIcon/LinkedinIcon";

import { MetaData } from "@/types/meta-data";

import { List } from "../../List/schema/list";
import { CreateJob, CreateJobSchema, Job, PreviewJobOffer } from "../schema/job";
import { LinkedinParams } from "../../LiPeople/components/LinkedinTagAutocomplete";
import { Suggestion } from "../../LiPeople/schema/liPeople";

import { useLocationSuggestions } from "./useLocationSuggestions";

interface CreateJobFormProps {
	job?: Job | null;
	list: List;
	handleSelect?: (job: Job | null) => void;
	handleDuplicate?: (job: Job | null) => void;
}

export const CreateJobForm: FunctionComponent<CreateJobFormProps> = ({
	job,
	list,
	handleSelect,
	handleDuplicate,
}) => {
	const initialState = {
		name: "",
		query: "",
		employmentType: EmploymentType.FULLTIME.toString(),
		jobScrapeType: ScrapeLevel.GET_INFO_AT,
		inTitle: false,
		repeatDaily: false,
	};

	const locationSuggestions = useLocationSuggestions();

	const { t } = useTranslation();
	const ts = (key: string) => t(`job.${key}`);

	const { setSearchValue: setEmploymentTypeSearchValue, searchValue: employmentTypeSearchValue } =
		useSearch();

	const [options, setOptions] = useState<
		{
			title: string;
			id: string;
		}[]
	>([]);

	const [update] = useUpdateJobMutation();
	const [createJob, { error, isLoading }] = useCreateJobMutation();
	const [location, setLocation] = useState<LinkedinParams>();

	const [positionTitles, setPositionTitles] = useState<
		{
			title: string;
			id: string;
		}[]
	>();
	const { query, setSearchValue, searchValue } = useSearch();

	const [currentFormState, setCurrentFormState] = useState<Partial<CreateJob>>(initialState);
	const [getFilterByValue, { isLoading: getFilterValueIsLoading }] =
		useLazyGetFilterByPropertyQuery();

	const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false);
	const [previewJobData, setPreviewJobData] = useState<PreviewJobOffer[]>([]);

	const [previewJobMeta, setPreviewJobMeta] = useState<MetaData>();

	const [previewJob, { isLoading: previewJobIsLoading }] = usePreviewJobMutation();

	const handlePreviewJob = async () => {
		const result = await previewJob({
			listId: list.id,
			query: currentFormState.query || "",
			country: currentFormState.location?.title || "",
			employmentType: currentFormState.employmentType || "",
			inTitle: currentFormState.inTitle || false,
		}).unwrap();

		if (result.data) {
			setPreviewJobData(result.data);
			setPreviewJobMeta(result.meta);
		}

		setShowPreviewModal(true);
	};

	const previewColumns: TableColumn[] = useMemo(
		() => [
			{
				title: "Name",
				uid: "name",
				render: (row: PreviewJobOffer) => (
					<div className="flex items-start justify-start gap-2">
						<div className="text-sm font-medium max-w-[300px] whitespace-nowrap overflow-hidden text-ellipsis">
							{row?.title}
						</div>
						<Link target="_blank" to={row?.linkedinUrl}>
							<Icon icon={faExternalLink} />
						</Link>
					</div>
				),
			},
			{
				title: "Company Name",
				uid: "companyName",
				render: (row: PreviewJobOffer) => (
					<div className="flex items-start justify-start gap-2">
						<div className="text-sm font-medium max-w-[300px] whitespace-nowrap overflow-hidden text-ellipsis">
							{row?.companyName}
						</div>
						<Link
							target="_blank"
							to={`https://www.linkedin.com/company/${row?.linkedinCompanyName}`}
						>
							<LinkedinIcon status="checked" />
						</Link>
					</div>
				),
			},
			{
				title: "Location",
				uid: "location",
				render: (row: PreviewJobOffer) => (
					<div className="flex items-start justify-start">{row?.location}</div>
				),
			},
			{
				title: "Posted",
				uid: "postedTimeAgo",
				render: (row: PreviewJobOffer) => (
					<div className="flex items-start justify-start">{row?.postedTimeAgo}</div>
				),
			},
		],
		[ts]
	);

	useEffect(() => {
		if (query) {
			const updateOptions = async (query: string) => {
				const result = await getFilterByValue({
					propertyName: "title",
					searchTerm: query,
				}).unwrap();

				setOptions(result?.data?.map((r) => ({ title: r.value, id: r.value })) || []);
			};

			updateOptions(query);
		}
	}, [getFilterByValue, query]);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleSaveFormState = (value: any, name: keyof CreateJob) => {
		let newValue = {
			...currentFormState,
			[name]: value,
		};

		if (name === "query") {
			newValue = {
				...newValue,
				name: value,
			};

			setValue("name", value, {
				shouldDirty: true,
				shouldValidate: true,
			});
		}

		setCurrentFormState(newValue);

		setValue(name, value, {
			shouldDirty: true,
			shouldValidate: true,
		});
	};

	const {
		reset,
		handleSubmit,
		setValue,
		formState: { errors },
	} = useForm<CreateJob>({
		defaultValues: initialState,
		resolver: zodResolver(CreateJobSchema),
	});

	useEffect(() => {
		if (job) {
			const formState = {
				name: job.name,
				query: job.query,
				employmentType: job.employmentType,
				jobScrapeType: job.jobScrapeType,
				repeatDaily: false,
				listId: list.id,
				inTitle: job.inTitle,
				location: job.location,
			};

			if (job.positionTitles) {
				setPositionTitles(
					job.positionTitles.map((r) => ({
						title: r,
						id: r,
					}))
				);
			}

			if (job.location) {
				setLocation({
					id: job.location.urn,
					name: job.location.title,
				} as LinkedinParams);
			}

			if (handleDuplicate) {
				reset({
					...formState,
				});
				setCurrentFormState({
					...formState,
				});
			} else {
				reset({
					id: job.id,
					...formState,
				});
				setCurrentFormState({
					...formState,
					id: job?.id,
				});
			}
		} else {
			reset();
			setLocation(undefined);
		}
	}, [job]);

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	useEffect(() => {
		if (list.scrapeLevel) {
			setValue("jobScrapeType", list.scrapeLevel as ScrapeLevel);
			setValue("repeatDaily", false);
		}
	}, [list.scrapeLevel]);

	const onSubmit = async (values: CreateJob) => {
		try {
			if (values.id && !handleDuplicate && location) {
				await update({
					id: values.id,
					...values,
					name: values.query,
					employmentType: values.employmentType,
					listId: list.id,
					location: { title: location.name, urn: location.id } as Suggestion,
					positionTitles: positionTitles?.map((r) => r.title) || [],
				}).unwrap();
			} else if (location) {
				await createJob({
					...values,
					name: values.query,
					employmentType: values.employmentType,
					listId: list.id,
					location: { title: location.name, urn: location.id } as Suggestion,
					positionTitles: positionTitles?.map((r) => r.title) || [],
				}).unwrap();
			}

			handleSelect?.(null);
			handleDuplicate?.(null);

			reset();
		} catch (err) {
			console.error(err);
		}
	};
	const buttonTitle =
		handleDuplicate && job?.id
			? t("basics.copyCriteria")
			: job?.id
				? t("basics.save")
				: t("basics.addCriteria");

	return (
		<>
			<div className="p-4 mt-8 rounded-xl border border-gray-200 bg-brand-white">
				<div className="text-md mb-5">Add a source</div>
				<div className="flex flex-row gap-4 w-full mb-4">
					<InputField
						error={!!errors.query?.message}
						handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
							handleSaveFormState(event.target.value, "query");
						}}
						label={"Search term"}
						name={"query"}
						placeholder={ts("queryPlaceholder")}
						value={currentFormState.query ?? ""}
					/>
				</div>
				<div className="flex flex-row gap-4 w-full mb-4">
					<AutoComplete
						disableLocalFilter
						data={locationSuggestions.options}
						handleReset={() => {
							setLocation(undefined);
							locationSuggestions.setSearchValue("");
							handleSaveFormState(undefined, "location");
						}}
						handleSearch={(value: string) => locationSuggestions.setSearchValue(value)}
						handleSelect={async (item?: AutoCompleteItemType) => {
							if (item) {
								setLocation({
									id: item.id.toString(),
									name: item.title,
								});
								locationSuggestions.setSearchValue("");
								handleSaveFormState(
									{
										title: item.title,
										urn: item.id,
									},
									"location"
								);
							} else {
								setLocation(undefined);
							}
						}}
						isLoading={locationSuggestions.isLoading}
						label={"Location"}
						statusText={
							locationSuggestions.failed ? t("errors.linkedinSuggestionsUnavailable") : undefined
						}
						selectedItem={currentFormState.location?.title || ""}
					/>
				</div>
				<div className="flex flex-col gap-2 w-full mb-4">
					<div className="w-full text-sm font-medium text-gray-900">Employment type</div>
					{!isLoading && (
						<Autocomplete
							multiple
							defaultValue={
								currentFormState?.employmentType?.split(",")?.map((r) => ({
									id: r,
									title: r.capitalizeString(),
								})) || undefined
							}
							getOptionLabel={(option) => option && option.title}
							inputValue={employmentTypeSearchValue || ""}
							isOptionEqualToValue={(option, currentValue) => {
								return option.id === currentValue.id;
							}}
							loading={false}
							loadingText="Loading..."
							options={[
								{
									title: EmploymentType.FULLTIME.capitalizeString(),
									id: EmploymentType.FULLTIME,
								},
								{
									title: EmploymentType.PARTTIME.capitalizeString(),
									id: EmploymentType.PARTTIME,
								},
								{
									title: EmploymentType.INTERN.capitalizeString(),
									id: EmploymentType.INTERN,
								},
								{
									title: EmploymentType.CONTRACTOR.capitalizeString(),
									id: EmploymentType.CONTRACTOR,
								},
								{
									title: EmploymentType.VOLUNTEER.capitalizeString(),
									id: EmploymentType.VOLUNTEER,
								},
								{
									title: EmploymentType.TEMPORARY.capitalizeString(),
									id: EmploymentType.TEMPORARY,
								},
							]}
							popupIcon={<Icon className="text-gray-500 h-[24px] w-[15px]" icon={faChevronDown} />}
							renderInput={(params) => (
								<TextField
									sx={{
										width: "100%",
										minHeight: 48,
										marginBottom: 1,
										"& .MuiFilledInput-root": {
											backgroundColor: "white",
											minHeight: 48,
											marginBottom: 0,
											borderRadius: 1.5,
											paddingTop: "10px",
											paddingBottom: "10px !important",
											paddingLeft: 1.5,
										},
									}}
									{...params}
								/>
							)}
							size="small"
							sx={{
								width: "100%",
							}}
							value={
								currentFormState?.employmentType?.split(",").map((r) => ({
									id: r,
									title: r.capitalizeString(),
								})) || []
							}
							onChange={(event, value) => {
								if (value && value?.length > 0) {
									handleSaveFormState(value.map((r) => r.id).join(","), "employmentType");
								} else {
									handleSaveFormState(null, "employmentType");
								}
							}}
							onInputChange={(event, newValue, reason) => {
								if (reason === "input") {
									setEmploymentTypeSearchValue(newValue);
								}
							}}
						/>
					)}
				</div>
				<div className="flex flex-col gap-4 w-full mb-4 mt-8">
					<div className="text-sm font-medium text-gray-900">Source settings</div>
					<div className="flex flex-row gap-2 items-center text-sm ">
						<Toggle
							isChecked={currentFormState.inTitle || false}
							onChange={() => {
								handleSaveFormState(!currentFormState.inTitle, "inTitle");
							}}
						/>

						<div>
							Search term in the title?{" "}
							<Information
								information={
									"Only consider job offers that include the search term in the title. This will significantly reduce the number of job offers to be scraped."
								}
							/>
						</div>
					</div>
				</div>
				{list.scrapeLevel === ScrapeLevel.FIND_PERSON_AT_COMPANY && (
					<div className="flex flex-col gap-4 w-full mb-4 mt-8">
						<Autocomplete
							multiple
							clearOnBlur={true}
							getOptionLabel={(option) => option.title}
							inputValue={searchValue || ""}
							isOptionEqualToValue={(option, currentValue) => {
								if (currentValue.title === "" && currentValue.id === "") {
									return true;
								}

								return option.id === currentValue.id;
							}}
							loading={getFilterValueIsLoading}
							loadingText="Loading..."
							options={
								[{ id: searchValue || "", title: searchValue || "" }, ...options]?.filter(
									(r) => r.id !== ""
								) || []
							}
							popupIcon={<Icon className="text-gray-500 h-[24px] w-[15px]" icon={faChevronDown} />}
							renderInput={(params) => (
								<TextField
									{...params}
									placeholder="Position title"
									sx={{
										width: "100%",
										minHeight: 48,
										marginBottom: 1,
										"& .MuiFilledInput-root": {
											minHeight: 48,
											marginBottom: 0,
											borderRadius: 1.5,
											paddingTop: "10px",
											paddingBottom: "10px !important",
											paddingLeft: 1.5,
										},
									}}
								/>
							)}
							size="small"
							sx={{
								width: "100%",
								backgroundColor: "white",
							}}
							value={positionTitles || []}
							onBlur={() => {
								setSearchValue("");
							}}
							onChange={(event, value) => {
								handleSaveFormState(
									value.map((r) => r.title),
									"positionTitles"
								);
								setPositionTitles(value);
								setSearchValue("");
							}}
							onInputChange={(event, newValue, reason) => {
								if (reason === "input") {
									setSearchValue(newValue);
								}
							}}
						/>
					</div>
				)}

				{getAllErrors(error, formErrors).length ? (
					<div className="mb-4">{renderErrorMessages(getAllErrors(error, formErrors))}</div>
				) : null}

				<div className="flex gap-4 w-full justify-end">
					{handleSelect && (
						<div className="flex max-w-[100px] w-full">
							<Button
								className="w-full"
								color={ButtonColor.ACTION_SECONDARY}
								title="cancel"
								onClick={() => {
									handleSelect(null);
									handleDuplicate?.(null);
									setCurrentFormState(initialState);
									reset(initialState);
								}}
							/>
						</div>
					)}
					<div className="flex w-fit">
						<Button
							className="w-full"
							color={ButtonColor.ACTION_SECONDARY}
							disabled={
								!currentFormState.query ||
								!currentFormState.location?.title ||
								!currentFormState.employmentType
							}
							isLoading={previewJobIsLoading}
							title="Preview"
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();

								handlePreviewJob();
							}}
						/>
					</div>
					<div className="flex max-w-[150px] w-full">
						<Button
							className="w-full"
							isLoading={isLoading}
							title={buttonTitle}
							onClick={handleSubmit(onSubmit)}
						/>
					</div>
				</div>
			</div>
			{showPreviewModal && (
				<Modal
					handleClose={() => setShowPreviewModal(false)}
					isOpened={showPreviewModal}
					size="lg"
					title="Preview"
				>
					<Table
						columns={previewColumns}
						data={previewJobData}
						hideFooter={true}
						hidePagination={true}
					/>

					{previewJobMeta && previewJobMeta.hasNextPage && (
						<div className="flex items-center justify-center">
							There are more than {previewJobMeta.itemCount} job offers available.
						</div>
					)}
					{previewJobMeta && !previewJobMeta.hasNextPage && (
						<div className="flex items-center justify-center">
							There are no more job offers available.
						</div>
					)}
				</Modal>
			)}
		</>
	);
};
